import { useMemo } from 'react';
import { Layers, Receipt, TrendingUp, Truck, Users } from 'lucide-react';
import { CategoryDonut, DailyTrendChart, RankedBarChart } from '../charts/Charts';
import { ChartCard, StatTile } from './StatsParts';
import { Spinner } from '../ui/Spinner';
import { LoadError } from '../common/LoadError';
import { useExpenses } from '../../hooks/useExpenses';
import type { Expense } from '../../types/expense';
import { formatearCOP } from '../../utils/expenses';
import {
  dailyAmounts, expensesByCategory, expensesByDriver, expensesByVehicle, resolveRange, summarizeExpenses,
} from '../../utils/dashboardStats';

interface ExpensesStatsPanelProps {
  from?: string;
  to?: string;
  periodLabel: string;
}

// Los importes son texto (`Money`): el gráfico recibe un `number` solo para
// dibujar la barra, y lo que se LEE — total, promedio, tooltip — sale
// formateado desde ahí, nunca de una suma en float64.
const pesos = (value: number) => formatearCOP(String(value));

// El centro de la dona mide ~110px: el importe exacto no cabe (`$ 6.459.567,89`
// se sale del aro). Ahí va la forma corta; el exacto está en el tile de arriba.
// Se arma a mano porque `notation: 'compact'` cambia de sufijo según el motor
// de ICU ("K" en Node, "mil" en el navegador).
const decimal = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 });
function pesosCompactos(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$ ${decimal.format(value / 1_000_000)} M`;
  if (Math.abs(value) >= 1_000) return `$ ${decimal.format(value / 1_000)} mil`;
  return pesos(value);
}

/**
 * Gastos del periodo. Se monta solo con la pestaña activa (ver
 * `LogbookStatsPanel`). El filtro va al servidor por `incurredAt`: el mes al
 * que pertenece un peaje es el de cuando se pagó, no el de cuando se digitó.
 */
export function ExpensesStatsPanel({ from, to, periodLabel }: ExpensesStatsPanelProps) {
  const { expenses, isLoading, error } = useExpenses({ from, to });

  if (error && expenses.length === 0) return <LoadError what="los gastos" error={error} />;
  if (isLoading) return <div className="dashboard-stats__loading"><Spinner size="lg" /></div>;
  return <ExpensesStatsView expenses={expenses} from={from} to={to} periodLabel={periodLabel} />;
}

/** La vista pura: recibe los gastos ya cargados. Separada para poder probarla con datos sintéticos. */
export function ExpensesStatsView({ expenses, from, to, periodLabel }: { expenses: Expense[] } & ExpensesStatsPanelProps) {
  const stats = useMemo(() => {
    const { start, end } = resolveRange(from, to, expenses.map(e => e.incurredAt));
    return {
      summary: summarizeExpenses(expenses),
      byCategory: expensesByCategory(expenses),
      byVehicle: expensesByVehicle(expenses, 6),
      byDriver: expensesByDriver(expenses, 6),
      trend: dailyAmounts(expenses, start, end),
    };
  }, [expenses, from, to]);

  const { summary } = stats;
  const topCategory = stats.byCategory[0];

  return (
    <div className="dashboard-stats">
      <div className="dashboard-stats__tiles">
        <StatTile label="Gasto total" value={formatearCOP(summary.total)} hint={`${summary.count} gasto${summary.count === 1 ? '' : 's'}`} />
        <StatTile
          label="Gasto promedio"
          value={summary.average === null ? '—' : pesos(summary.average)}
          hint="por gasto registrado"
        />
        <StatTile
          label="Categoría mayor"
          value={topCategory?.label ?? '—'}
          hint={topCategory ? pesos(topCategory.value) : undefined}
        />
        <StatTile
          label="Sin recibo"
          value={summary.withoutReceipt}
          hint={summary.count > 0 ? `de ${summary.count}` : undefined}
        />
      </div>

      <section className="dashboard-page__charts">
        <ChartCard title={`Por categoría — ${periodLabel}`} icon={<Layers size={18} />} empty={summary.count === 0}>
          <CategoryDonut data={stats.byCategory} centerLabel="gastado" centerValue={pesosCompactos(Number(summary.total))} valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title={`Gasto por día — ${periodLabel}`} icon={<TrendingUp size={18} />} empty={summary.count === 0}>
          <DailyTrendChart data={stats.trend} color="#F59E0B" seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title="Vehículos con más gasto" icon={<Truck size={18} />} empty={stats.byVehicle.length === 0}>
          <RankedBarChart data={stats.byVehicle} seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title="Conductores con más gasto" icon={<Users size={18} />} empty={stats.byDriver.length === 0}>
          <RankedBarChart data={stats.byDriver} seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
      </section>

      <p className="dashboard-stats__note">
        <Receipt size={13} /> Los gastos sin vehículo (p. ej. viáticos) cuentan en el total y en la categoría, pero no en el ranking de vehículos.
      </p>
    </div>
  );
}
